import { useEffect, useRef } from 'react';
import { showAosNoticeToast } from '../components/common/AosNoticeToast';

const TOAST_DEBOUNCE_MS = 2000;

const TYPING_TOAST_ID = 'xss-input-warning';
const PASTE_TOAST_ID = 'xss-paste-blocked';

const XSS_WARNING_MESSAGE =
	'Special characters < > are not allowed in this field.';

const TEXT_INPUT_TYPES = new Set([
	'text',
	'email',
	'tel',
	'search',
	'url',
	'password',
]);

export function hasDangerousChar(text: string): boolean {
	return text.includes('<') || text.includes('>');
}

function isProtectedInput(target: EventTarget | null): target is HTMLInputElement {
	if (!(target instanceof HTMLInputElement)) return false;
	if (target.dataset.allowHtml !== undefined) return false;
	if (target.dataset.passwordGuard !== undefined) return false;
	const type = target.type.toLowerCase();
	return TEXT_INPUT_TYPES.has(type);
}

export function useXssInputProtection(): void {
	const lastTypingToastTime = useRef(0);
	const lastPasteToastTime = useRef(0);

	const showTypingWarning = () => {
		const now = Date.now();
		if (now - lastTypingToastTime.current <= TOAST_DEBOUNCE_MS) {
			return;
		}

		lastTypingToastTime.current = now;
		showAosNoticeToast({
			type: 'error',
			message: XSS_WARNING_MESSAGE,
			toastId: TYPING_TOAST_ID,
			autoClose: 8000,
			position: 'top-center',
		});
	};

	const showPasteBlockedWarning = () => {
		const now = Date.now();
		if (now - lastPasteToastTime.current <= TOAST_DEBOUNCE_MS) {
			return;
		}

		lastPasteToastTime.current = now;
		showAosNoticeToast({
			type: 'error',
			message: XSS_WARNING_MESSAGE,
			toastId: PASTE_TOAST_ID,
			autoClose: 8000,
			position: 'top-center',
		});
	};

	const rejectClipboardText = (raw: string, event: ClipboardEvent | DragEvent) => {
		if (!hasDangerousChar(raw)) {
			return;
		}

		event.preventDefault();
		showPasteBlockedWarning();
	};

	/**
	 * Clears a value the browser already wrote (autocomplete / autofill).
	 * Uses the native value setter so React-controlled inputs stay in sync.
	 */
	const clearDangerousInputValue = (input: HTMLInputElement) => {
		const valueDescriptor = Object.getOwnPropertyDescriptor(
			HTMLInputElement.prototype,
			'value'
		);
		valueDescriptor?.set?.call(input, '');
		input.dispatchEvent(new Event('input', { bubbles: true }));
		showPasteBlockedWarning();
	};

	useEffect(() => {
		/** Prevents re-entry when we dispatch a synthetic `input` after clearing. */
		let isClearingDangerousValue = false;

		const handleKeyDown = (event: KeyboardEvent) => {
			if (!isProtectedInput(event.target)) return;
			if (event.key === '<' || event.key === '>') {
				event.preventDefault();
				showTypingWarning();
			}
		};

		const handlePaste = (event: ClipboardEvent) => {
			if (!isProtectedInput(event.target)) return;

			const raw = event.clipboardData?.getData('text') ?? '';
			rejectClipboardText(raw, event);
		};

		const handleDrop = (event: DragEvent) => {
			if (!isProtectedInput(event.target)) return;

			const raw = event.dataTransfer?.getData('text') ?? '';
			rejectClipboardText(raw, event);
		};

		const handleInput = (event: Event) => {
			if (isClearingDangerousValue) return;
			if (!isProtectedInput(event.target)) return;

			const input = event.target;
			const value = input.value;
			if (!hasDangerousChar(value)) return;

			isClearingDangerousValue = true;
			try {
				clearDangerousInputValue(input);
			} finally {
				isClearingDangerousValue = false;
			}
		};

		document.addEventListener('keydown', handleKeyDown, true);
		document.addEventListener('paste', handlePaste, true);
		document.addEventListener('drop', handleDrop, true);
		document.addEventListener('input', handleInput, true);

		return () => {
			document.removeEventListener('keydown', handleKeyDown, true);
			document.removeEventListener('paste', handlePaste, true);
			document.removeEventListener('drop', handleDrop, true);
			document.removeEventListener('input', handleInput, true);
		};
	}, []);
}
